import { CheckCircle, FileText, CaretRight } from '@phosphor-icons/react';

import Text from '../../../components/atoms/Text';
import Heading from '../../../components/atoms/Heading';
import PrimaryButton from '../../../components/atoms/Button';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../shared/constants/routes';
import { EVENT_TYPES } from '../../../shared/constants/events';
import { sendEvent } from '../../../services/user-interaction.service';
import { ANALYTICS_EVENTS } from '../../../shared/constants/events';
import useClarityEvent from '../../../hooks/useClarityEvent';
type ApprovalCardProps = {
	month: string;
	year: string;
	platform: string;
	amount: number;
	fftId: number;
	status: string;
	length: number;
	viewHome?: boolean;
	user_id: number;
};

export const ApprovalCard = ({
	month,
	year,
	platform,
	amount,
	fftId,
	status,
	length,
	viewHome = false,
	user_id,
}: ApprovalCardProps) => {
	const navigate = useNavigate();
	const zeroBalanceMessages = [
		'Confirmamos ingresos y deducciones equilibrados',
		'Verificamos retenciones compensan impuesto causado',
		'Validamos cálculo de impuesto neto',
	];
	const negativeBalanceMessages = [
		'Verificamos ingresos totales del periodo',
		'Calculamos deducciones aplicables',
		'Hemos determinado tus impuestos a pagar',
	];
	const positiveBalanceMessages = [
		'Analizamos todas las retenciones aplicables',
		'Verificamos monto de deducciones autorizadas',
		'Confirmamos cálculo de saldo a favor',
	];
	let infoMessage;
	if (amount === 0) {
		infoMessage = zeroBalanceMessages;
	} else if (amount > 0) {
		infoMessage = positiveBalanceMessages;
	} else {
		infoMessage = negativeBalanceMessages;
	}
	const { trackEvent } = useClarityEvent();
	const handleNavigate = () => {
		if (status === 'approved' || status === 'automatic') {
			navigate(ROUTES.DETAIL_APPROVAL + `?fftId=${fftId}&viewHome=${viewHome}`);
		} else if (status === 'denied') {
			navigate(
				ROUTES.DENIED_DECLARATIONS + `?fftId=${fftId}&viewHome=${viewHome}`,
			);
		} else {
			navigate(
				ROUTES.APPROVAL_DECLARATIONS + `?fftId=${fftId}&viewHome=${viewHome}`,
			);
			viewDetailApprovation();
		}
	};

	const viewDetailApprovation = async () => {
		if (!user_id) return;

		try {
			await sendEvent({
				user_id: user_id,
				event: ANALYTICS_EVENTS.APPROVAL_DECLARATIONS,
				source: 'web',
				event_payload: {
					subsection: EVENT_TYPES.APPROVAL_DECLARATIONS_VIEW_DETAIL,
				},
			});
			trackEvent(EVENT_TYPES.APPROVAL_DECLARATIONS_VIEW_DETAIL);
		} catch (error) {}
	};

	return (
		<div
			className={`${status === 'denied' || status === 'approved' || status === 'automatic' ? 'pt-0 px-4 pb-4' : 'p-4'} ${length === 1 ? 'w-auto' : 'w-72'} ${viewHome ? 'border border-h-neutral-90' : ''} bg-white mt-[16px] rounded-lg inline-flex flex-col justify-start items-start gap-6 snap-center shrink-0`}
		>
			{status === 'denied' && (
				<div className='self-stretch px-2 py-1 bg-orange-400 rounded-bl-lg rounded-br-lg inline-flex justify-center items-center gap-2'>
					<Text
						size='mini-1'
						weight='medium'
						sx='text-white'
					>
						Revision de declaración en curso{' '}
					</Text>
				</div>
			)}
			{(status === 'approved' || status === 'automatic') && (
				<div className='self-stretch px-2 py-1 bg-h-primary rounded-bl-lg rounded-br-lg inline-flex justify-center items-center gap-2'>
					<Text
						size='mini-1'
						weight='medium'
						sx='text-white'
					>
						Presentando tu declaración ante el SAT
					</Text>
				</div>
			)}
			<div>
				<Text
					size='body-3'
					weight='regular'
				>
					{month} {year}
				</Text>
				{!viewHome && (
					<Text
						size='caption'
						weight='light'
					>
						{platform}
					</Text>
				)}
			</div>
			<div className='flex items-end gap-2'>
				<div
					className={`p-1.5 rounded-full inline-flex justify-start items-center gap-2.5 ${amount === 0 ? 'bg-h-neutral-90' : amount > 0 ? 'bg-h-success-90' : 'bg-h-primary-90'}`}
				>
					<FileText
						size={27}
						color={
							amount === 0 ? '#4D4D4D' : amount > 0 ? '#65a30d' : '#1772C2'
						}
					/>
				</div>
				<div>
					<Text
						size='mini-1'
						weight='light'
					>
						Declaración calculada, resultado:{' '}
					</Text>
					<div className='flex items-end gap-2'>
						<Heading
							size='s'
							weight='regular'
						>
							$
							{Math.abs(amount).toLocaleString('es-MX', {
								maximumFractionDigits: 0,
								minimumFractionDigits: 0,
							})}{' '}
						</Heading>
						<Text
							size='mini-1'
							weight='medium'
						>
							{amount === 0 ? 'en cero' : amount > 0 ? 'a favor' : 'a pagar'}
						</Text>
					</div>
				</div>
			</div>
			{!viewHome && (
				<div className='self-stretch p-4 bg-h-neutral-94 rounded-lg inline-flex flex-col justify-center items-start gap-4'>
					<Text
						size='caption'
						weight='medium'
					>
						Elementos verificados
					</Text>
					{infoMessage.map((item) => (
						<div
							key={item}
							className='flex items-center gap-2'
						>
							<div>
								<CheckCircle
									size={16}
									color='var(--h-success-50)'
									weight='duotone'
								/>
							</div>
							<Text
								size='mini-1'
								weight='light'
							>
								{item}{' '}
							</Text>
						</div>
					))}
				</div>
			)}
			<PrimaryButton
				labelColor={status === 'pending' ? 'white' : 'primary'}
				sx={status === 'pending' ? '' : 'justify-end w-full'}
				onClick={handleNavigate}
				label={status === 'pending' ? 'Ver detalle y aprobar' : 'Ver detalle'}
				variant={status === 'pending' ? 'primary' : 'text'}
				icon={
					status === 'pending' ? (
						<CheckCircle size={24} />
					) : (
						<CaretRight size={24} />
					)
				}
			/>
		</div>
	);
};
